import React from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { Link } from 'react-router-dom';
import moment from 'moment';
import ProfileHeaderActions from './ProfileHeaderActions';

const ProfileDetailsCard = ({ user, countryObj, t }) => {
    if (!user || !user.UserType) return null;

    const actualUser = (user && user.user) ? user.user : user;
    const nameFromParts = `${actualUser.FirstName || ''} ${actualUser.SecondName || ''} ${actualUser.LastName || ''}`.trim();
    const fullName = nameFromParts.length > 0 ? nameFromParts : (actualUser.Name || actualUser.FullName || actualUser.Username || 'المستخدم');

    const photoUrl = actualUser.Photo ? actualUser.Photo : "/assets/images/teacher/teacher-single.jpg";
    const userTypeId = actualUser.UserType ? actualUser.UserType.Id : null;
    const userTypeName = actualUser.UserType ? actualUser.UserType.Name : "";

    const dateOneMonthAhead = moment().add(1, 'M');
    const activateUntilDateMoment = actualUser.ActivateUntilDate
        ? moment(actualUser.ActivateUntilDate, "DD/MM/YYYY")
        : null;

    const isNearRenewal = userTypeId === 2 || userTypeId === 3;
    const shouldShowRenewalBtn = isNearRenewal &&
        !(userTypeId === 3 && actualUser.School && actualUser.School.Id === 1) &&
        activateUntilDateMoment && activateUntilDateMoment <= dateOneMonthAhead;

    return (
        <div className="profile-hero-card mb-4">
            {/* Top Cover Header Banner */}
            <div className="profile-cover-header">
                <div className="profile-actions-header-wrapper">
                    <ProfileHeaderActions userType={actualUser.UserType} t={t} />
                </div>
            </div>

            {/* Profile Main Body */}
            <div className="profile-card-body px-4 px-md-5 pb-5">
                <div className="d-flex align-items-end gap-3 flex-wrap mt-2 mb-4 profile-card-header">
                    {/* 1. Standalone Avatar Container Div */}
                    <div className="profile-avatar-container-div">
                        <div className="profile-avatar-outer">
                            <div className="profile-avatar-inner">
                                <LazyLoadImage
                                    src={photoUrl}
                                    alt={fullName}
                                    className="profile-avatar-image"
                                />
                                {actualUser.IsActive && <span className="profile-online-indicator" title={t('Pages.Profile.IsActive.Active')}></span>}
                            </div>
                            <Link to="/Profile/Picture" className="profile-avatar-camera-btn" title={t('ChangePicture')}>
                                <i className="fal fa-camera"></i>
                            </Link>
                        </div>
                    </div>

                    {/* 2. Standalone Identity Text Container Div */}
                    <div className="profile-identity-text-container-div mr-4">
                        <div className="profile-identity-text">
                            <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                                <h1 className="profile-display-name mb-0">{fullName}</h1>
                                <i className="fas fa-check-circle profile-verified-badge" title="حساب موثق"></i>
                            </div>

                            <div className="profile-badges-strip mt-2">
                                {userTypeName && (
                                    <span className="profile-type-badge">
                                        <i className="fal fa-user-shield ml-1"></i>
                                        {userTypeName}
                                    </span>
                                )}

                                {actualUser.School && actualUser.School.Name && (
                                    <span className="profile-school-badge">
                                        <i className="fal fa-school ml-1"></i>
                                        {actualUser.School.Name}
                                    </span>
                                )}

                                {actualUser.IsActive === true ? (
                                    <span className="profile-status-badge status-active">
                                        <span className="pulse-dot active ml-1"></span>
                                        {t('Pages.Profile.IsActive.Active')}
                                    </span>
                                ) : (
                                    <span className="profile-status-badge status-inactive">
                                        <span className="pulse-dot inactive ml-1"></span>
                                        {t('Pages.Profile.IsActive.NotActive')}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {/* Structured Info Grid Cards */}
                <div className="profile-info-tiles-grid">
                    {/* Username */}
                    <div className="info-tile-card">
                        <div className="tile-icon-box text-indigo">
                            <i className="fal fa-user-tag"></i>
                        </div>
                        <div className="tile-content-box">
                            <span className="tile-label">{t('Pages.Register.Username.title')}</span>
                            <span className="tile-value">{actualUser.Username}</span>
                        </div>
                    </div>

                    {/* Birthday */}
                    <div className="info-tile-card">
                        <div className="tile-icon-box text-rose">
                            <i className="fal fa-calendar-alt"></i>
                        </div>
                        <div className="tile-content-box">
                            <span className="tile-label">{t('Pages.Register.Birthday.title')}</span>
                            <span className="tile-value">{actualUser.Birthday || '-'}</span>
                        </div>
                    </div>

                    {/* Gender / Sex */}
                    {actualUser.Sex && (
                        <div className="info-tile-card">
                            <div className="tile-icon-box text-sky">
                                <i className={`fad fa-${actualUser.Sex}`}></i>
                            </div>
                            <div className="tile-content-box">
                                <span className="tile-label">{t('Pages.Register.Sex.title')}</span>
                                <span className="tile-value">{t('Pages.Register.Sex.' + actualUser.Sex)}</span>
                            </div>
                        </div>
                    )}

                    {/* Country */}
                    {countryObj && countryObj.value !== 'il' && (
                        <div className="info-tile-card">
                            <div className="tile-icon-box text-amber">
                                <i className="fal fa-globe-asia"></i>
                            </div>
                            <div className="tile-content-box">
                                <span className="tile-label">{t('Pages.Register.Country.title')}</span>
                                <span className="tile-value">{countryObj.label}</span>
                            </div>
                        </div>
                    )}

                    {/* Address */}
                    <div className="info-tile-card">
                        <div className="tile-icon-box text-emerald">
                            <i className="fal fa-map-marker-alt"></i>
                        </div>
                        <div className="tile-content-box">
                            <span className="tile-label">{t('Pages.Register.Address.title')}</span>
                            <span className="tile-value">{actualUser.Address || '-'}</span>
                        </div>
                    </div>

                    {/* Email */}
                    {actualUser.Email && (
                        <div className="info-tile-card">
                            <div className="tile-icon-box text-purple">
                                <i className="fal fa-envelope"></i>
                            </div>
                            <div className="tile-content-box">
                                <span className="tile-label">{t('Pages.Register.Email.title')}</span>
                                <a href={`mailto:${actualUser.Email}`} className="tile-link">
                                    {actualUser.Email}
                                </a>
                            </div>
                        </div>
                    )}

                    {/* Phone */}
                    <div className="info-tile-card">
                        <div className="tile-icon-box text-teal">
                            <i className="fal fa-phone-alt"></i>
                        </div>
                        <div className="tile-content-box">
                            <span className="tile-label">{t('Pages.Register.Phone.title')}</span>
                            <a href={`Tel:${actualUser.Phone}`} className="tile-link">
                                <span dir="ltr">{actualUser.Phone || '-'}</span>
                            </a>
                        </div>
                    </div>

                    {/* Last Login */}
                    <div className="info-tile-card">
                        <div className="tile-icon-box text-blue">
                            <i className="fal fa-history"></i>
                        </div>
                        <div className="tile-content-box">
                            <span className="tile-label">{t('Pages.Profile.LastLogin.title')}</span>
                            <span className="tile-value">{actualUser.LastLogin || '-'}</span>
                        </div>
                    </div>

                    {/* Subscription Expiry Date */}
                    {isNearRenewal && (
                        <div className="info-tile-card full-width-mobile">
                            <div className="tile-icon-box text-emerald">
                                <i className="fal fa-clock"></i>
                            </div>
                            <div className="tile-content-box">
                                <span className="tile-label">{t('Pages.Profile.ActivateUntilDate.title')}</span>
                                {actualUser.ActivateUntilDate !== "0" ? (
                                    <span className="tile-value text-emerald font-weight-bold">
                                        {actualUser.ActivateUntilDate}
                                    </span>
                                ) : (
                                    <span className="tile-value text-rose font-weight-bold">
                                        {t('Pages.Profile.ActivateUntilDate.NotActive')}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Subscription Renewal CTA Banner */}
                {shouldShowRenewalBtn && (
                    <div className="profile-renewal-banner-card mt-4">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 p-4">
                            <div className="d-flex align-items-center gap-3">
                                <div className="renewal-icon-circle">
                                    <i className="fal fa-funnel-dollar display-5 text-emerald"></i>
                                </div>
                                <div>
                                    <h4 className="renewal-title mb-1">
                                        {actualUser.ActivateUntilDate !== "0"
                                            ? t('Pages.Profile.services.renewal')
                                            : t('Pages.Profile.services.active')}
                                    </h4>
                                    <p className="renewal-desc mb-0">جدد اشتراكك الآن لتستمتع بكافة خدمات وميزات المنصة بدون انقطاع.</p>
                                </div>
                            </div>
                            <Link to="/User/Renwal" className="profile-cta-glow-btn">
                                <i className="fal fa-bolt ml-1"></i>
                                <span>{actualUser.ActivateUntilDate !== "0" ? t('Pages.Profile.services.renewal') : t('Pages.Profile.services.active')}</span>
                            </Link>
                        </div>
                    </div>
                )}

                {/* School Activation Alert Notice */}
                {userTypeId === 3 && actualUser.School && actualUser.School.Id === 1 && (
                    <div className="profile-modern-alert-card alert-school mt-4 p-4">
                        <div className="d-flex align-items-start gap-3">
                            <i className="fal fa-exclamation-triangle display-4 alert-icon"></i>
                            <div>
                                <h4 className="alert-title">{t('Pages.Profile.ActivateSchool.title')}</h4>
                                <p className="alert-body mb-0">{t('Pages.Profile.ActivateSchool.body')}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* SubUsers Banner */}
                {actualUser.ActivateUntilDate !== "0" && (userTypeId === 2 || userTypeId === 6) && (
                    <div className="profile-modern-alert-card alert-subusers mt-4 p-4">
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                            <div className="d-flex align-items-center gap-2">
                                <i className="fad fa-users display-5 text-indigo"></i>
                                <span className="font-weight-bold text-slate-800">{t('Pages.SubUsers.addSubUser')}:</span>
                            </div>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <Link to="/User/Control" className="alert-link-btn primary">
                                    <i className="fad fa-game-board-alt ml-1"></i>
                                    {t('Pages.UserControl.title')}
                                </Link>
                                <i className="fad fa-chevron-left text-muted"></i>
                                <Link to="/User/SubUsers" className="alert-link-btn secondary">
                                    <i className="fad fa-users ml-1"></i>
                                    {t('Pages.UserControl.subUsers')}
                                </Link>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProfileDetailsCard;
